import React, { useEffect, useState } from "react";
import { Card, Table, Button, Space, Tag, Timeline, message, Modal, Form, Select, InputNumber, Input } from "antd";
import { PlusOutlined, NodeIndexOutlined } from "@ant-design/icons";
import { scGetBatches, scCreateBatch, scBatchTimeline, scGetParties, scGetItems } from "../../api/client";
import { byStr } from "../../utils/sorters";
import { toJalali } from "../../utils/dateUtils";
import { useAuth } from "../../context/AuthContext";
import { eventLabel, eventColor, statusMeta, fmtKg } from "./scConstants";
import EmptyWorkArea from "../../components/EmptyWorkArea";
import GxTable from "../../components/GxTable";

const { Option } = Select;

const STAGE_LABEL = { yarn: "نخ", raw_fabric: "پارچهٔ خام", dyed_fabric: "پارچهٔ رنگ‌شده", warehouse_received: "واردِ انبار" };

export default function BatchTrackingPage() {
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [parties, setParties] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [timeline, setTimeline] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try { const r = await scGetBatches(); setRows(r.data); }
    catch (e) { message.error(e?.response?.data?.detail || "خطا"); }
    setLoading(false);
  };
  useEffect(() => {
    load();
    scGetParties().then((r) => setParties(r.data)).catch(() => {});
    scGetItems().then((r) => setItems(r.data)).catch(() => {});
  }, []);

  const openTimeline = async (id) => {
    try { const r = await scBatchTimeline(id); setTimeline(r.data); }
    catch (e) { message.error(e?.response?.data?.detail || "خطا"); }
  };

  const submit = async () => {
    const v = await form.validateFields();
    try {
      await scCreateBatch(v);
      message.success("بچ ایجاد شد");
      setModalOpen(false); form.resetFields(); load();
    } catch (e) { message.error(e?.response?.data?.detail || "خطا"); }
  };

  const columns = [
    { title: "کد بچ", dataIndex: "batch_code", sorter: byStr("batch_code") },
    { title: "مرحله", dataIndex: "material_stage", render: (v) => <Tag>{STAGE_LABEL[v] || v}</Tag> },
    { title: "نزد", dataIndex: "current_party_name", render: (v) => v || "—" },
    { title: "کالا", dataIndex: "item_name", render: (v) => v || "—" },
    { title: "وزن فعلی", dataIndex: "current_weight_kg", render: (v) => fmtKg(v) },
    { title: "وضعیت", dataIndex: "status", render: (v) => <Tag color={v === "active" ? "blue" : "default"}>{v}</Tag> },
    { title: "", width: 110, render: (_, r) => <Button size="small" onClick={() => openTimeline(r.id)}>تایم‌لاین</Button> },
  ];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>ردیابی بچ</h2>
        {can("sc.event.create") && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => { form.resetFields(); setModalOpen(true); }}>بچ جدید</Button>
        )}
      </div>

      <Card>
        {rows.length === 0
          ? <EmptyWorkArea small message="هنوز بچی ثبت نشده است" />
          : <GxTable dataSource={rows} columns={columns} rowKey="id" size="small" loading={loading}
              pagination={{ pageSize: 15 }} scroll={{ x: "max-content" }} />}
      </Card>

      <Modal title={timeline ? `تایم‌لاین بچ ${timeline.batch.batch_code}` : ""} open={!!timeline}
        footer={null} onCancel={() => setTimeline(null)} width={560}>
        {timeline && (timeline.timeline.length === 0
          ? <EmptyWorkArea small message="رویدادی برای این بچ ثبت نشده است" />
          : <Timeline
              items={timeline.timeline.map((e) => ({
                color: eventColor(e.event_type) === "default" ? "gray" : undefined,
                children: (
                  <div>
                    <Tag color={eventColor(e.event_type)}>{eventLabel(e.event_type)}</Tag>
                    <Tag color={statusMeta(e.status).c}>{statusMeta(e.status).l}</Tag>
                    <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
                      {toJalali(e.event_date)} — {fmtKg(e.weight_kg)}
                      {e.source_party_name && ` — از ${e.source_party_name}`}
                      {e.destination_party_name && ` → ${e.destination_party_name}`}
                    </div>
                  </div>
                ),
              }))} />
        )}
      </Modal>

      <Modal title="بچ جدید" open={modalOpen} onOk={submit} onCancel={() => setModalOpen(false)}
        okText="ایجاد" cancelText="انصراف">
        <Form form={form} layout="vertical" style={{ direction: "rtl" }} initialValues={{ material_stage: "yarn" }}>
          <Form.Item name="material_stage" label="مرحلهٔ ماده" rules={[{ required: true }]}>
            <Select options={Object.entries(STAGE_LABEL).map(([v, l]) => ({ value: v, label: l }))} />
          </Form.Item>
          <Form.Item name="current_party_id" label="نزد کدام طرف">
            <Select allowClear showSearch optionFilterProp="children">
              {parties.map((p) => <Option key={p.id} value={p.id}>{p.name}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="item_type_id" label="نوع کالا">
            <Select allowClear options={items.map((i) => ({ value: i.id, label: i.name }))} />
          </Form.Item>
          <Space wrap>
            <Form.Item name="item_name" label="نام کالا"><Input style={{ width: 160 }} /></Form.Item>
            <Form.Item name="color" label="رنگ"><Input style={{ width: 110 }} /></Form.Item>
            <Form.Item name="initial_weight_kg" label="وزن اولیه"><InputNumber min={0} style={{ width: 130 }} /></Form.Item>
          </Space>
          <Form.Item name="notes" label="یادداشت"><Input.TextArea rows={2} /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
